import Link from 'next/link'
import * as React from 'react'

import { cn } from '@/lib/utils'

import { CourseDetailDialog } from './course-detail-dialog'

import type { NonFsdsGeneralV1CourseItem } from '@/types/non-fsds-general-v1.type'

interface CourseJourneySectionProps {
  title: string
  description: string
  courses: NonFsdsGeneralV1CourseItem[]
  className?: string
}

/**
 * General V1 Course Journey — horizontal timeline + five-card track.
 *
 * One `activeIndex` drives timeline progress, step highlight and card
 * emphasis. Cards are approximately 400px wide with partial edge cropping
 * matching the Figma composition. Keyboard arrows and click both update
 * the index. Step numbers are derived from array order so Puck reorder
 * stays correct.
 */
export function CourseJourneySection({
  title,
  description,
  courses,
  className,
}: CourseJourneySectionProps) {
  const [activeIndex, setActiveIndex] = React.useState(0)
  const [detailCourse, setDetailCourse] = React.useState<NonFsdsGeneralV1CourseItem | null>(null)
  const trackRef = React.useRef<HTMLDivElement>(null)
  const tabRefs = React.useRef<(HTMLButtonElement | null)[]>([])
  const pendingFocusIndexRef = React.useRef<number | null>(null)

  React.useEffect(() => {
    setActiveIndex(prev => Math.min(prev, Math.max(courses.length - 1, 0)))
  }, [courses.length])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    let newIndex = activeIndex
    if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault()
      newIndex = Math.max(0, activeIndex - 1)
    } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault()
      newIndex = Math.min(courses.length - 1, activeIndex + 1)
    }
    if (newIndex !== activeIndex) {
      pendingFocusIndexRef.current = newIndex
      setActiveIndex(newIndex)
    }
  }

  React.useEffect(() => {
    const pendingIndex = pendingFocusIndexRef.current
    if (pendingIndex === null) return
    tabRefs.current[pendingIndex]?.focus()
    pendingFocusIndexRef.current = null
  }, [activeIndex])

  // Scroll active card into view in the track
  React.useEffect(() => {
    const track = trackRef.current
    if (!track || typeof track.scrollTo !== 'function') return
    const cards = track.children
    if (!cards[activeIndex]) return
    const card = cards[activeIndex] as HTMLElement
    const trackRect = track.getBoundingClientRect()
    const cardRect = card.getBoundingClientRect()
    const scrollLeft = card.offsetLeft - trackRect.width / 2 + cardRect.width / 2
    track.scrollTo({ left: scrollLeft, behavior: 'smooth' })
  }, [activeIndex])

  if (!courses || courses.length === 0) return null

  const visibleStart = Math.min(
    Math.max(activeIndex - 1, 0),
    Math.max(courses.length - 3, 0)
  )
  const visibleCourses = courses.slice(visibleStart, visibleStart + 3)

  const activeVisibleIndex = activeIndex - visibleStart
  const progressPercent = ((activeVisibleIndex * 2 + 1) / (visibleCourses.length * 2)) * 100

  return (
    <>
      <section
        className={cn('bg-[var(--gv1-bg-surface)] pb-[15px] pt-[30px]', className)}
        data-testid="gv1-course-journey"
      >
      <div className="gv1-container">
        {/* Horizontal Timeline */}
        <div
          className="relative mb-[26px]"
          role="tablist"
          aria-label={title}
          onKeyDown={handleKeyDown}
        >
          <div className="relative mx-auto grid w-[1224px] max-w-full grid-cols-3 gap-3">
            <div className="pointer-events-none absolute inset-x-0 top-5 col-span-3 h-0.5 bg-gray-200" aria-hidden="true">
              <div
                className="h-full bg-[var(--gv1-accent)] motion-safe:transition-all motion-safe:duration-500"
                style={{ width: `${progressPercent}%` }}
              />
            </div>

            {visibleCourses.map((course, visibleIndex) => {
              const index = visibleStart + visibleIndex
              const isActive = index === activeIndex
              const isPast = index <= activeIndex

              return (
                <button
                  key={course.id}
                  ref={el => { tabRefs.current[index] = el }}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  aria-controls={`course-panel-${course.id}`}
                  id={`course-tab-${course.id}`}
                  onClick={() => setActiveIndex(index)}
                  className="group flex flex-col items-center outline-none"
                  tabIndex={isActive ? 0 : -1}
                >
                  <div
                    className={cn(
                      'relative z-10 flex size-10 items-center justify-center rounded-full border-2 text-sm font-semibold motion-safe:transition-all motion-safe:duration-300',
                      isActive
                        ? 'border-[var(--gv1-accent)] bg-[var(--gv1-accent)] text-white scale-110'
                        : isPast
                          ? 'border-[var(--gv1-accent)] bg-white text-[var(--gv1-accent)]'
                          : 'border-gray-200 bg-white text-gray-400 group-hover:border-gray-300',
                      'focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--gv1-accent)]'
                    )}
                  >
                    {index + 1}
                  </div>
                </button>
              )
            })}
          </div>
        </div>
      </div>

      {/* Card Track — full-width with overflow for edge cropping */}
      <div className="relative overflow-hidden" aria-label={`${title}. ${description}`}>
        <div
          ref={trackRef}
          className="flex gap-3 overflow-x-auto pb-4 scrollbar-hide snap-x snap-mandatory"
          style={{
            scrollbarWidth: 'none',
            paddingInline: 'max(16px, calc((100vw - var(--gv1-content-width)) / 2))',
          }}
        >
          {courses.map((course, index) => {
            const isActive = index === activeIndex

            return (
              <div
                key={course.id}
                id={`course-panel-${course.id}`}
                role="tabpanel"
                aria-labelledby={`course-tab-${course.id}`}
                aria-hidden={!isActive}
                onClick={() => setActiveIndex(index)}
                className={cn(
                  'min-h-[310px] w-[360px] shrink-0 snap-center cursor-pointer rounded-[var(--gv1-radius-card)] border bg-white p-[var(--gv1-card-padding)] motion-safe:transition-all motion-safe:duration-300 lg:w-[400px]',
                  isActive
                    ? 'border-[var(--gv1-accent)] shadow-md'
                    : 'border-gray-200/50 opacity-70 hover:opacity-90'
                )}
                style={{ borderWidth: 'var(--gv1-border-thin)' }}
              >
                {/* Keyword chips */}
                {course.keywords && course.keywords.length > 0 && (
                  <div className="mb-4 flex flex-wrap gap-2">
                    {course.keywords.slice(0, 3).map((kw, ki) => (
                      <span
                        key={ki}
                        className="rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-[var(--gv1-accent)]"
                      >
                        {kw}
                      </span>
                    ))}
                  </div>
                )}

                {/* Course name */}
                <h3
                  className="mb-1 text-lg font-bold text-[var(--gv1-text-heading)]"
                  style={{ fontFamily: 'var(--gv1-font-heading)' }}
                >
                  {course.name}
                </h3>

                {/* Course code */}
                {course.courseCode && (
                  <p className="mb-3 text-xs text-[var(--gv1-text-muted)]">
                    {course.courseCode}
                  </p>
                )}

                {/* Meta: timeline + level */}
                <div className="mb-4 flex items-center gap-4 text-sm text-[var(--gv1-text-body)]">
                  {course.timeline && (
                    <span className="flex items-center gap-1.5">
                      <CalendarIcon />
                      {course.timeline}
                    </span>
                  )}
                  {course.level && (
                    <span className="flex items-center gap-1.5">
                      <ChartIcon />
                      {course.level}
                    </span>
                  )}
                </div>

                {/* Highlights */}
                {course.highlights && course.highlights.length > 0 && (
                  <ul className="mb-6 space-y-2">
                    {course.highlights.slice(0, 3).map((hl, hi) => (
                      <li key={hi} className="flex items-start gap-2 text-sm text-[var(--gv1-text-body)]">
                        <CheckIcon />
                        <span>{hl}</span>
                      </li>
                    ))}
                  </ul>
                )}

                {/* Actions */}
                <div className="mt-auto flex items-center gap-3">
                  {course.detailUrl && (
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation()
                        setDetailCourse(course)
                      }}
                      tabIndex={isActive ? undefined : -1}
                      aria-label={`View details for ${course.name}`}
                      className={cn(
                        'rounded-full px-5 py-2 text-sm font-medium transition-colors',
                        isActive
                          ? 'bg-[var(--gv1-accent)] text-white hover:bg-[var(--gv1-accent)]/90'
                          : 'bg-gray-100 text-[var(--gv1-text-body)] hover:bg-gray-200'
                      )}
                    >
                      View details
                    </button>
                  )}
                  {course.contactUrl && isActive && (
                    <Link
                      href={course.contactUrl}
                      className="rounded-full border border-gray-200 px-5 py-2 text-sm font-medium text-[var(--gv1-text-body)] transition-colors hover:bg-gray-50"
                    >
                      Contact
                    </Link>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>
        <CourseDetailDialog
          course={detailCourse}
          portalContainer={trackRef.current?.ownerDocument.body}
          onOpenChange={open => {
            if (!open) setDetailCourse(null)
          }}
        />
      </section>
      <div
        data-gv1-community-feedback-slot
        data-testid="gv1-community-feedback-slot"
      />
    </>
  )
}

/* ---------- Inline icons ---------- */

function CalendarIcon() {
  return (
    <svg className="size-4 shrink-0 text-[var(--gv1-text-muted)]" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
    </svg>
  )
}

function ChartIcon() {
  return (
    <svg className="size-4 shrink-0 text-[var(--gv1-text-muted)]" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z" />
    </svg>
  )
}

function CheckIcon() {
  return (
    <svg className="mt-0.5 size-4 shrink-0 text-[var(--gv1-accent)]" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
      <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clipRule="evenodd" />
    </svg>
  )
}
